import { apiClient } from '../../services/apiClient';

export type MediaPlatform = {
  id: string;
  code: string;
  display_name: string;
  description?: string | null;
  sort_order: number;
  is_active: boolean;
};

export type MediaMenu = {
  id: string;
  code: string;
  display_name: string;
  description?: string | null;
  platform_id: string;
  platform_code?: string | null;
  platform_display_name?: string | null;
  media_type: string;
  guidelines: string[];
  // 画像本体にテキスト/ロゴを焼き込まない方が良いメニューかどうか。
  // 生成フォームでメニュー選択時に「テキストを含めない ON」「ロゴを追加する OFF」を促すモーダル表示に使う。
  require_no_text?: boolean;
  require_no_logo?: boolean;
  // プラットフォーム選択時に自動選択されるデフォルトメニューかどうか。
  is_default?: boolean;
  sort_order: number;
  is_active: boolean;
};

export type AssetSpecBase = {
  id: string;
  code: string;
  display_name: string;
  description?: string | null;
  size_constraint_type: string;
  width_px?: number | null;
  height_px?: number | null;
  min_width_px?: number | null;
  max_width_px?: number | null;
  min_height_px?: number | null;
  max_height_px?: number | null;
  allowed_formats: string[];
  allowed_aspect_ratios: string[];
  max_file_size_bytes?: number | null;
  guidelines: string[];
  menu_guidelines: string[];
  menu_id?: string | null;
  menu_code?: string | null;
  menu_display_name?: string | null;
  platform_id?: string | null;
  platform_code?: string | null;
  platform_display_name?: string | null;
  origin: 'global' | 'custom';
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
};

export type ImageAssetSpec = AssetSpecBase & {
  media_type?: 'image';
  dpi?: number | null;
  // 媒体の推奨サイズ (必須条件ではない)。生成画面の推奨未達警告と自動リサイズ目標の初期値に使う。
  // longest_edge_range では幅=高さ=推奨長辺。両方 null = 推奨なし。
  // docs/requirements/media-spec-recommended-size-warning-plan.md §3.1
  recommended_width_px?: number | null;
  recommended_height_px?: number | null;
  // 規格寸法の2倍 (×2) での出力を生成画面で選べるか。false = 広告サイズと同じ寸法しか入稿できない媒体
  // (Google ディスプレイ アップロード型 等)。未設定は true 扱い
  allow_double_output?: boolean;
  // メニューと同様、画像仕様単位でも「テキスト/ロゴを焼き込まない」推奨を持てる。
  require_no_text?: boolean;
  require_no_logo?: boolean;
};

export type VideoAssetSpec = AssetSpecBase & {
  media_type: 'video';
  allowed_codecs?: string[];
  allowed_audio_codecs?: string[];
  min_duration_seconds?: number | null;
  max_duration_seconds?: number | null;
  min_bitrate_kbps?: number | null;
  max_bitrate_kbps?: number | null;
  min_framerate?: number | null;
  max_framerate?: number | null;
  audio_required?: boolean | null;
};

export type AssetSpec = ImageAssetSpec | VideoAssetSpec;

export function isVideoSpec(spec: AssetSpec): spec is VideoAssetSpec {
  return spec.media_type === 'video';
}

type ListResponse<T> = {
  items?: T[];
};

export async function fetchMediaPlatforms(): Promise<MediaPlatform[]> {
  const { data } = await apiClient.get<ListResponse<MediaPlatform>>('/media/platforms');
  return data.items ?? [];
}

export async function fetchMediaMenus(platformId: string): Promise<MediaMenu[]> {
  const { data } = await apiClient.get<ListResponse<MediaMenu>>(`/media/platforms/${platformId}/menus`);
  return data.items ?? [];
}

export async function fetchImageSpecs(menuId: string): Promise<ImageAssetSpec[]> {
  const { data } = await apiClient.get<ListResponse<ImageAssetSpec>>(`/media/menus/${menuId}/specs`);
  return data.items ?? [];
}

export async function fetchImageSpecById(specId: string): Promise<ImageAssetSpec | null> {
  const { data } = await apiClient.get<ImageAssetSpec>(`/media/specs/${specId}`);
  return data ?? null;
}

export async function fetchAssetSpecs(menuId: string): Promise<AssetSpec[]> {
  const { data } = await apiClient.get<ListResponse<AssetSpec>>(`/media/menus/${menuId}/specs`);
  return data.items ?? [];
}

export async function fetchAssetSpecById(specId: string): Promise<AssetSpec | null> {
  const { data } = await apiClient.get<AssetSpec>(`/media/specs/${specId}`);
  return data ?? null;
}

/**
 * 画像素材が画像仕様に適合するかチェックする
 */
export function checkMaterialMatchesSpec(
  material: { width?: number; height?: number; content_type?: string; file_size_bytes?: number },
  spec: ImageAssetSpec
): boolean {
  // 画像形式のチェック（allowed_formatsが空の場合はスキップ）
  if (spec.allowed_formats && spec.allowed_formats.length > 0) {
    if (!material.content_type) return false;
    
    // content_typeから拡張子を推定 (例: "image/jpeg" -> "jpg", "image/png" -> "png")
    const contentTypeMap: Record<string, string[]> = {
      'image/jpeg': ['jpg', 'jpeg'],
      'image/png': ['png'],
      'image/gif': ['gif'],
      'image/webp': ['webp'],
      'image/tiff': ['tif', 'tiff'],
      'image/bmp': ['bmp'],
    };
    
    const allowedExtensions = contentTypeMap[material.content_type.toLowerCase()] || [];
    const hasMatchingFormat = spec.allowed_formats.some(format => 
      allowedExtensions.includes(format.toLowerCase())
    );
    
    if (!hasMatchingFormat) return false;
  }

  // 幅と高さがない場合は判定不可
  if (!material.width || !material.height) return false;

  // サイズ制約のチェック
  switch (spec.size_constraint_type) {
    case 'fixed':
      // 固定サイズ
      if (spec.width_px && material.width !== spec.width_px) return false;
      if (spec.height_px && material.height !== spec.height_px) return false;
      // fixedでもmin/maxが設定されている場合はチェック
      if (spec.min_width_px && material.width < spec.min_width_px) return false;
      if (spec.max_width_px && material.width > spec.max_width_px) return false;
      if (spec.min_height_px && material.height < spec.min_height_px) return false;
      if (spec.max_height_px && material.height > spec.max_height_px) return false;
      break;

    case 'min':
      // 最小サイズ
      if (spec.min_width_px && material.width < spec.min_width_px) return false;
      if (spec.min_height_px && material.height < spec.min_height_px) return false;
      break;

    case 'max':
      // 最大サイズ
      if (spec.max_width_px && material.width > spec.max_width_px) return false;
      if (spec.max_height_px && material.height > spec.max_height_px) return false;
      break;

    case 'range':
      // 範囲指定
      if (spec.min_width_px && material.width < spec.min_width_px) return false;
      if (spec.max_width_px && material.width > spec.max_width_px) return false;
      if (spec.min_height_px && material.height < spec.min_height_px) return false;
      if (spec.max_height_px && material.height > spec.max_height_px) return false;
      break;

    case 'longest_edge_min':
      // 最長辺の最小値チェック（例：最長辺が500px以上）
      {
        const longestEdge = Math.max(material.width, material.height);
        if (spec.min_width_px && longestEdge < spec.min_width_px) return false;
      }
      break;

    case 'longest_edge_max':
      // 最長辺の最大値チェック（例：最長辺が10000px以下）
      {
        const longestEdge = Math.max(material.width, material.height);
        if (spec.max_width_px && longestEdge > spec.max_width_px) return false;
      }
      break;

    case 'longest_edge_range':
      // 最長辺の範囲チェック（例：最長辺が500px以上10000px以下）
      {
        const longestEdge = Math.max(material.width, material.height);
        if (spec.min_width_px && longestEdge < spec.min_width_px) return false;
        if (spec.max_width_px && longestEdge > spec.max_width_px) return false;
      }
      break;

    case 'aspect_ratio_only':
    case 'min_with_aspect':
    case 'max_with_aspect':
      // アスペクト比のチェック（allowed_aspect_ratiosが空の場合はスキップ）
      if (spec.allowed_aspect_ratios && spec.allowed_aspect_ratios.length > 0) {
        const materialAspectRatio = material.width / material.height;
        const hasMatchingAspect = spec.allowed_aspect_ratios.some(ratioStr => {
          // "1:1" や "16:9" などの形式をパース
          const [w, h] = ratioStr.split(':').map(Number);
          if (!w || !h) return false;
          const targetRatio = w / h;
          // 誤差0.01以内であればマッチとみなす
          return Math.abs(materialAspectRatio - targetRatio) < 0.01;
        });
        if (!hasMatchingAspect) return false;
      }

      // min_with_aspect や max_with_aspect の場合はサイズもチェック
      if (spec.size_constraint_type === 'min_with_aspect') {
        if (spec.min_width_px && material.width < spec.min_width_px) return false;
        if (spec.min_height_px && material.height < spec.min_height_px) return false;
      } else if (spec.size_constraint_type === 'max_with_aspect') {
        if (spec.max_width_px && material.width > spec.max_width_px) return false;
        if (spec.max_height_px && material.height > spec.max_height_px) return false;
      }
      break;

    default:
      // 未知の制約タイプの場合はスキップ
      break;
  }

  // ファイルサイズのチェック
  if (spec.max_file_size_bytes && material.file_size_bytes) {
    if (material.file_size_bytes > spec.max_file_size_bytes) return false;
  }

  return true;
}

/**
 * 画像素材が複数の仕様のいずれかに適合するかチェックする
 */
export function checkMaterialMatchesAnySpec(
  material: { width?: number; height?: number; content_type?: string; file_size_bytes?: number },
  specs: ImageAssetSpec[]
): boolean {
  if (!specs || specs.length === 0) return true; // 仕様がない場合はすべて表示
  return specs.some(spec => checkMaterialMatchesSpec(material, spec));
}

type VideoMaterialProbe = {
  width?: number;
  height?: number;
  content_type?: string;
  file_size_bytes?: number;
  duration_seconds?: number;
  bitrate_kbps?: number;
  framerate?: number;
  codec?: string;
};

export function checkVideoMaterialMatchesSpec(
  material: VideoMaterialProbe,
  spec: VideoAssetSpec,
): boolean {
  if (spec.allowed_formats && spec.allowed_formats.length > 0 && material.content_type) {
    const ct = material.content_type.toLowerCase();
    const contentTypeMap: Record<string, string[]> = {
      'video/mp4': ['mp4', 'mpeg4'],
      'video/quicktime': ['mov'],
      'video/webm': ['webm'],
      'video/x-msvideo': ['avi'],
      'video/3gpp': ['3gp'],
      'video/x-flv': ['flv'],
      'video/mpeg': ['mpeg', 'mpg'],
    };
    const allowedExts = contentTypeMap[ct] || [];
    const hasFormat = spec.allowed_formats.some((f) => allowedExts.includes(f.toLowerCase()));
    if (!hasFormat) return false;
  }

  if (material.width && material.height) {
    switch (spec.size_constraint_type) {
      case 'fixed':
        if (spec.width_px && material.width !== spec.width_px) return false;
        if (spec.height_px && material.height !== spec.height_px) return false;
        break;
      case 'min':
      case 'min_with_aspect':
        if (spec.min_width_px && material.width < spec.min_width_px) return false;
        if (spec.min_height_px && material.height < spec.min_height_px) return false;
        break;
      case 'max':
      case 'max_with_aspect':
        if (spec.max_width_px && material.width > spec.max_width_px) return false;
        if (spec.max_height_px && material.height > spec.max_height_px) return false;
        break;
      case 'range':
        if (spec.min_width_px && material.width < spec.min_width_px) return false;
        if (spec.max_width_px && material.width > spec.max_width_px) return false;
        if (spec.min_height_px && material.height < spec.min_height_px) return false;
        if (spec.max_height_px && material.height > spec.max_height_px) return false;
        break;
    }

    if (
      ['aspect_ratio_only', 'min_with_aspect', 'max_with_aspect'].includes(spec.size_constraint_type) &&
      spec.allowed_aspect_ratios &&
      spec.allowed_aspect_ratios.length > 0
    ) {
      const materialRatio = material.width / material.height;
      const matched = spec.allowed_aspect_ratios.some((r) => {
        const [w, h] = r.split(':').map(Number);
        if (!w || !h) return false;
        return Math.abs(materialRatio - w / h) < 0.01;
      });
      if (!matched) return false;
    }
  }

  if (spec.max_file_size_bytes && material.file_size_bytes && material.file_size_bytes > spec.max_file_size_bytes) {
    return false;
  }

  if (material.duration_seconds != null) {
    if (spec.min_duration_seconds != null && material.duration_seconds < spec.min_duration_seconds) return false;
    if (spec.max_duration_seconds != null && material.duration_seconds > spec.max_duration_seconds) return false;
  }

  if (material.bitrate_kbps != null) {
    if (spec.min_bitrate_kbps != null && material.bitrate_kbps < spec.min_bitrate_kbps) return false;
    if (spec.max_bitrate_kbps != null && material.bitrate_kbps > spec.max_bitrate_kbps) return false;
  }

  if (material.framerate != null) {
    if (spec.min_framerate != null && material.framerate < spec.min_framerate) return false;
    if (spec.max_framerate != null && material.framerate > spec.max_framerate) return false;
  }

  if (material.codec && spec.allowed_codecs && spec.allowed_codecs.length > 0) {
    const codec = material.codec.toLowerCase();
    if (!spec.allowed_codecs.some((c) => c.toLowerCase() === codec)) return false;
  }

  return true;
}
